import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, uploadAvatar } from './client'
import type {
  AlerteCouchage, Assignation, Categorie, Chambre, Compte, Couple, Depense,
  NuitsResp, Parametre, Personne, RecapNuitees, RecapRepas, RecapReglements,
  RecapSoldes, RepasResp,
} from './types'

export const useMe = () =>
  useQuery({ queryKey: ['me'], queryFn: () => api.get<{ authenticated: boolean }>('/auth/me'), retry: false })

export const useLogin = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (password: string) => api.post('/auth/login', { password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
}

export const useLogout = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => qc.clear(),
  })
}

export const useComptes = () =>
  useQuery({ queryKey: ['comptes'], queryFn: () => api.get<Compte[]>('/comptes') })

export const useCreateCompte = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { nom: string; rang: number }) => api.post<Compte>('/comptes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comptes'] }),
  })
}
export const useUpdateCompte = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; nom: string; rang: number }) =>
      api.patch<Compte>(`/comptes/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comptes'] }),
  })
}
export const useDeleteCompte = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del(`/comptes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comptes'] })
      qc.invalidateQueries({ queryKey: ['personnes'] })
    },
  })
}

export const usePersonnes = () =>
  useQuery({ queryKey: ['personnes'], queryFn: () => api.get<Personne[]>('/personnes') })

export const useCreatePersonne = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { compte_id: number; prenom: string; categorie: Categorie; ordre?: number }) =>
      api.post<Personne>('/personnes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personnes'] }),
  })
}
export const useUpdatePersonne = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<{ compte_id: number; prenom: string; categorie: Categorie; actif: boolean; ordre: number }>) =>
      api.patch<Personne>(`/personnes/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personnes'] }),
  })
}
export const useDeletePersonne = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      api.del(`/personnes/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personnes'] }),
  })
}

export const useUploadAvatar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      uploadAvatar<Personne>(`/personnes/${id}/avatar`, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personnes'] }),
  })
}

export const useDeleteAvatar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<Personne>(`/personnes/${id}/avatar`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personnes'] }),
  })
}

export const useNuits = () =>
  useQuery({ queryKey: ['nuits'], queryFn: () => api.get<NuitsResp>('/nuits') })

export const useSetPresenceNuit = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      personne_id: number; cible_id: number;
      present: boolean; tentatif?: boolean; refus?: boolean;
    }) => api.put('/nuits/presence', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nuits'] })
      qc.invalidateQueries({ queryKey: ['recap'] })
      // Une coche de présence peut entraîner une attribution auto de chambre
      qc.invalidateQueries({ queryKey: ['assignations'] })
      qc.invalidateQueries({ queryKey: ['couchages', 'alertes'] })
      // …et synchronise automatiquement les repas (nuit → M+S veille, M lendemain)
      qc.invalidateQueries({ queryKey: ['repas'] })
      qc.invalidateQueries({ queryKey: ['menus'] })
    },
  })
}

export const useRepas = () =>
  useQuery({ queryKey: ['repas'], queryFn: () => api.get<RepasResp>('/repas') })

export const useMenus = () =>
  useQuery({
    queryKey: ['menus'],
    queryFn: () => api.get<import('./types').MenusResp>('/menus'),
    // La météo est cachée 1 h côté serveur ; inutile de marteler l'endpoint.
    staleTime: 5 * 60_000,
  })

export const useReglagesMenus = () =>
  useQuery({
    queryKey: ['menus', 'reglages'],
    queryFn: () => api.get<import('./types').ReglagesMenus>('/menus/reglages'),
  })

export const useSetReglagesMenus = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      objectif_cout_repas: number | null;
      adresse_maison: string;
      rayon_recherche_km: number;
      prefs_sejour?: string[];
    }) => api.put<import('./types').ReglagesMenus>('/menus/reglages', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menus'] })          // re-score avec l'objectif
      qc.invalidateQueries({ queryKey: ['menus', 'reglages'] })
      qc.invalidateQueries({ queryKey: ['menus', 'commerces'] })
    },
  })
}

export const useSetMenuSelection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { repas_id: number; menu_id: string | null }) =>
      api.put('/menus/selection', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

export const useEchangerMenus = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      repas_id_a: number; repas_id_b: number;
      menu_id_a: string; menu_id_b: string;
    }) => api.post('/menus/echange', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

export const useSetPrefsJour = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { date: string; prefs: string[] }) =>
      api.put('/menus/prefs-jour', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

export const useCreateMenuPerso = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      nom: string; description?: string; moment?: string;
      prix_adulte?: number;
      ingredients: { nom: string; qte: number; unite: string }[];
      recette?: string;
      repas_id?: number | null;
    }) => api.post<{ ok: boolean; id: string }>('/menus/perso', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

export const useDeleteMenuPerso = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (persoId: number) => api.del(`/menus/perso/${persoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

export const useSetPrefsRepas = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { repas_id: number; prefs: string[] }) =>
      api.put('/menus/prefs-repas', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

/** Responsable(s) d'un repas : envoie la liste COMPLÈTE (ajout/retrait incrémental côté UI). */
export const useSetResponsables = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { repas_id: number; personne_ids: number[] }) =>
      api.put('/menus/responsables', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

/** Responsables d'une JOURNÉE entière : remplace midi ET soir (min 1 personne). */
export const useSetResponsablesJour = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { date: string; personne_ids: number[] }) =>
      api.put('/menus/responsables/jour', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

/** Attribution AUTO : un binôme adultes/ados par journée (midi + soir), équitable
 *  selon la durée de présence. Écrase les attributions existantes. */
export const useAutoResponsables = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{
      ok: boolean;
      jours: { date: string; personne_ids: number[]; prenoms: string[] }[];
      repartition: { personne_id: number; prenom: string; jours_presence: number; jours_attribues: number }[];
    }>('/menus/responsables/auto'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })
}

/** Suggestion automatique d'ingrédients d'après le nom du menu (par adulte). */
export const fetchSuggestionIngredients = (nom: string) =>
  api.get<{
    source: 'catalogue' | 'connu' | 'generique' | 'aucune';
    match: string | null;
    prix_adulte: number | null;
    ingredients: { nom: string; qte: number; unite: string }[];
  }>(`/menus/perso/suggestion-ingredients?nom=${encodeURIComponent(nom)}`)

export const useToggleFavoriCommerce = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { osm_id: string; favori: boolean }) =>
      api.put('/menus/commerces/favori', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus', 'commerces'] }),
  })
}

export const useCommerces = () =>
  useQuery({
    queryKey: ['menus', 'commerces'],
    queryFn: () => api.get<import('./types').CommercesResp>('/menus/commerces'),
    // Les commerces sont cachés 24 h côté serveur.
    staleTime: 30 * 60_000,
  })

/** Autocomplete d'adresse — appel direct (pas de useQuery : piloté par debounce). */
export const fetchAdresseSuggestions = (q: string) =>
  api.get<{ suggestions: import('./types').AdresseSuggestion[] }>(
    `/menus/adresse-suggestions?q=${encodeURIComponent(q)}`,
  )

/** Itinéraires maison → point. Query par destination, cachée 24 h côté serveur. */
export const useItineraire = (lat: number | null, lon: number | null) =>
  useQuery({
    queryKey: ['menus', 'itineraire', lat, lon],
    queryFn: () => api.get<import('./types').ItineraireResp>(`/menus/itineraire?lat=${lat}&lon=${lon}`),
    enabled: lat != null && lon != null,
    staleTime: 30 * 60_000,
    retry: 1,
  })

export const useSetPresenceRepas = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { personne_id: number; cible_id: number; present: boolean }) =>
      api.put('/repas/presence', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['repas'] })
      qc.invalidateQueries({ queryKey: ['recap'] })
      qc.invalidateQueries({ queryKey: ['menus'] })
    },
  })
}

export const useDepenses = () =>
  useQuery({ queryKey: ['depenses'], queryFn: () => api.get<Depense[]>('/depenses') })

export const useCreateDepense = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { date: string; compte_id: number; montant: number; libelle: string }) =>
      api.post<Depense>('/depenses', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depenses'] })
      qc.invalidateQueries({ queryKey: ['recap'] })
    },
  })
}
export const useUpdateDepense = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<{ date: string; compte_id: number; montant: number; libelle: string }>) =>
      api.patch<Depense>(`/depenses/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depenses'] })
      qc.invalidateQueries({ queryKey: ['recap'] })
    },
  })
}
export const useDeleteDepense = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del(`/depenses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depenses'] })
      qc.invalidateQueries({ queryKey: ['recap'] })
    },
  })
}

export const useParametre = (annee: number) =>
  useQuery({ queryKey: ['parametre', annee], queryFn: () => api.get<Parametre>(`/parametres/${annee}`) })

export const useSetHeroPosition = (annee: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (hero_position: number) =>
      api.put<Parametre>(`/parametres/${annee}/hero`, { hero_position }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parametre', annee] }),
  })
}

export const useRecapNuitees = () =>
  useQuery({ queryKey: ['recap', 'nuitees'], queryFn: () => api.get<RecapNuitees>('/recap/nuitees') })

export const useRecapRepas = () =>
  useQuery({ queryKey: ['recap', 'repas'], queryFn: () => api.get<RecapRepas>('/recap/repas') })

export const useRecapSoldes = () =>
  useQuery({ queryKey: ['recap', 'soldes'], queryFn: () => api.get<RecapSoldes>('/recap/soldes') })

export const useRecapReglements = () =>
  useQuery({ queryKey: ['recap', 'reglements'], queryFn: () => api.get<RecapReglements>('/recap/reglements') })

// === Couchages ===
export const useChambres = () =>
  useQuery({ queryKey: ['chambres'], queryFn: () => api.get<Chambre[]>('/couchages/chambres') })

export const useCouples = () =>
  useQuery({ queryKey: ['couples'], queryFn: () => api.get<Couple[]>('/couchages/couples') })

export const useAssignations = () =>
  useQuery({ queryKey: ['assignations'], queryFn: () => api.get<Assignation[]>('/couchages/assignations') })

export const useAlertesCouchages = () =>
  useQuery({ queryKey: ['couchages', 'alertes'], queryFn: () => api.get<AlerteCouchage[]>('/couchages/alertes') })

export const useAssigner = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { personne_id: number; chambre_id: number | null; nuit_id?: number | null }) =>
      api.put<{ updated: number }>('/couchages/assignations', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignations'] })
      qc.invalidateQueries({ queryKey: ['couchages', 'alertes'] })
    },
  })
}

export const useAutoCouchages = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/couchages/auto'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignations'] })
      qc.invalidateQueries({ queryKey: ['couchages', 'alertes'] })
    },
  })
}

export const useFillCouchages = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ filled: number; skipped: number }>('/couchages/fill'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignations'] })
      qc.invalidateQueries({ queryKey: ['couchages', 'alertes'] })
    },
  })
}

// === Lits ===
const invalidateLitsRelated = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['chambres'] })           // capacite a changé
  qc.invalidateQueries({ queryKey: ['couchages', 'alertes'] }) // alertes sur-capacité peuvent changer
}

export const useCreateLit = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { chambre_id: number; type: import('./types').TypeLit }) =>
      api.post<import('./types').Lit>('/couchages/lits', body),
    onSuccess: () => invalidateLitsRelated(qc),
  })
}

export const useUpdateLit = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { type?: import('./types').TypeLit; ordre?: number } }) =>
      api.patch<import('./types').Lit>(`/couchages/lits/${id}`, body),
    onSuccess: () => invalidateLitsRelated(qc),
  })
}

export const useDeleteLit = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del(`/couchages/lits/${id}`),
    onSuccess: () => invalidateLitsRelated(qc),
  })
}
